import React from 'react';
import './ui.css';

/**
 * Button — the canonical action control for the app.
 *
 * Replaces the ~25 bespoke button classes (.btn-primary, .save-button,
 * .create-button, .action-button, …) that had drifted apart across features.
 * Consumes design tokens from App.css, so every button shares one set of
 * colors, sizes, radii, focus rings, and press feedback.
 *
 * Props:
 *   variant: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
 *            (default 'primary')
 *   size:    'sm' | 'md' | 'lg' (default 'md')
 *   block:   boolean — stretch to full width
 *   as:      element/tag to render (default 'button'); pass 'a' for links
 *   className: extra classes appended after the ui-btn classes
 *   ...rest: forwarded to the underlying element (onClick, type, href,
 *            disabled, aria-*, etc.)
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  as: Tag = 'button',
  className = '',
  type,
  children,
  ...rest
}) {
  const classes = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    block ? 'ui-btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Only native <button> takes a type; default it to "button" so these never
  // accidentally submit a surrounding form.
  const typeProp = Tag === 'button' ? { type: type || 'button' } : {};

  return (
    <Tag className={classes} {...typeProp} {...rest}>
      {children}
    </Tag>
  );
}
